import { z } from 'zod';
import { ApproachEvidenceKindSchema, ApproachIdSchema, Confidence, entity, IdSchema, IsoDateTimeSchema } from './common';
import { ClinicalRoleSchema } from './session';

// ---------- ClinicalEvidence (Amendment A4) ----------

export const EvidenceTypeSchema = z.enum(['FACT', 'CLIENT_REPORT', 'COUNSELOR_OBSERVATION', 'HYPOTHESIS']);
export type EvidenceType = z.infer<typeof EvidenceTypeSchema>;

export const EvidenceSourceSchema = z.enum([
  'transcript',
  'counselor-memo',
  'external-record',
  'ai-analysis',
  'user-entry',
]);
export type EvidenceSource = z.infer<typeof EvidenceSourceSchema>;

export const ClinicalEvidenceSchema = entity('ClinicalEvidence', {
  clientId: IdSchema,
  sessionId: IdSchema.optional(),

  evidenceType: EvidenceTypeSchema,
  category: z.string().optional(),
  text: z.string().min(1),

  sourceSession: z.number().int().min(1).optional(),
  sourceTimeMs: z.number().int().min(0).optional(),
  sourceText: z.string().optional(),
  sourceSegmentIds: z.array(z.string()).optional(),
  roleAtExtraction: ClinicalRoleSchema.optional(),
  roleConfidenceAtExtraction: Confidence.optional(),
  agreementStrength: z.enum(['explicit', 'weak-agreement']).optional(),

  confidence: Confidence.optional(),
  verified: z.boolean(),
  needsVerification: z.boolean(),
  author: z.enum(['counselor', 'ai', 'system']),
  source: EvidenceSourceSchema,
});
export type ClinicalEvidence = z.infer<typeof ClinicalEvidenceSchema>;

// ---------- SessionAnalysis ----------

export const AnalysisItemSchema = z
  .object({
    /** revision 간 유지되는 가설 식별자 (Amendment A5) */
    id: IdSchema,
    text: z.string().min(1),
    status: z.enum(['observation', 'hypothesis', 'confirmed']),
    evidenceIds: z.array(IdSchema).optional(),
    confidence: Confidence.optional(),
  })
  .strict();
export type AnalysisItem = z.infer<typeof AnalysisItemSchema>;

export const InterventionUseSchema = z
  .object({
    id: IdSchema,
    interventionName: z.string().min(1),
    theoreticalOrientation: z.string().optional(),
    rationale: z.string().optional(),
    counselorAction: z.string().optional(),
    clientResponse: z.string().optional(),
    effectiveness: z.string().optional(),
    followUpNeeded: z.boolean().optional(),
    evidenceIds: z.array(IdSchema).optional(),
  })
  .strict();
export type InterventionUse = z.infer<typeof InterventionUseSchema>;

/** Amendment A33: 치료 접근 근거 (판단 자료, 사실 아님) */
export const ApproachEvidenceSchema = z
  .object({
    id: IdSchema,
    approach: ApproachIdSchema,
    kind: ApproachEvidenceKindSchema,
    quote: z.string().min(1).max(1000),
    reason: z.string().max(1000).optional(),
    segmentIds: z.array(z.string().max(40)).max(20).optional(),
    excluded: z.boolean().optional(),
  })
  .strict();
export type ApproachEvidence = z.infer<typeof ApproachEvidenceSchema>;

export const SessionAnalysisSchema = entity('SessionAnalysis', {
  clientId: IdSchema,
  sessionId: IdSchema,
  formatVersion: z.literal('SESSION_ANALYSIS_V1'),

  sessionSummary: z.string().optional(),
  keyEvents: z.array(z.string()).optional(),
  notableChanges: z.array(z.string()).optional(),

  emotions: z.array(AnalysisItemSchema).optional(),
  cognitions: z.array(AnalysisItemSchema).optional(),
  behaviors: z.array(AnalysisItemSchema).optional(),
  relationshipPatterns: z.array(AnalysisItemSchema).optional(),

  interventions: z.array(InterventionUseSchema).optional(),
  counselorClientInteraction: z.array(z.string()).optional(),
  /** Amendment A33 */
  approachEvidence: z.array(ApproachEvidenceSchema).optional(),

  riskUpdateId: IdSchema.optional(),
  goalProgressIds: z.array(IdSchema).optional(),
  formulationChangeProposalId: IdSchema.optional(),
  diagnosticUpdateProposalId: IdSchema.optional(),
  nextSessionBriefId: IdSchema.optional(),

  source: z.enum(['chatgpt', 'claude', 'manual']),
  importedRawText: z.string().optional(),
});
export type SessionAnalysis = z.infer<typeof SessionAnalysisSchema>;

// ---------- Risk ----------

export const RiskItemSchema = z
  .object({
    status: z.enum(['not_assessed', 'denied', 'present', 'unclear']),
    severity: z.enum(['low', 'moderate', 'high', 'critical']).optional(),
    evidenceIds: z.array(IdSchema).optional(),
    actionTaken: z.string().optional(),
    referralNeeded: z.boolean().optional(),
    followUpRequired: z.boolean().optional(),
  })
  .strict();
export type RiskItem = z.infer<typeof RiskItemSchema>;

export const RISK_KEYS = [
  'suicidalIdeation',
  'suicidalPlan',
  'suicidalIntent',
  'selfHarm',
  'homicidalIdeation',
  'violenceRisk',
  'abuseOrNeglect',
  'substanceRisk',
  'acutePsychiatricRisk',
] as const;
export type RiskKey = (typeof RISK_KEYS)[number];

export const RiskAssessmentSchema = entity('RiskAssessment', {
  clientId: IdSchema,
  sessionId: IdSchema,
  suicidalIdeation: RiskItemSchema,
  suicidalPlan: RiskItemSchema,
  suicidalIntent: RiskItemSchema,
  selfHarm: RiskItemSchema,
  homicidalIdeation: RiskItemSchema,
  violenceRisk: RiskItemSchema,
  abuseOrNeglect: RiskItemSchema,
  substanceRisk: RiskItemSchema,
  acutePsychiatricRisk: RiskItemSchema,
  overallNote: z.string().optional(),
  counselorApproved: z.boolean(),
});
export type RiskAssessment = z.infer<typeof RiskAssessmentSchema>;

// ---------- Goals ----------

export const GoalStatusSchema = z.enum([
  'not_started',
  'exploring',
  'progressing',
  'maintaining',
  'achieved',
  'paused',
  'terminated',
]);
export type GoalStatus = z.infer<typeof GoalStatusSchema>;

export const CounselingGoalSchema = entity('CounselingGoal', {
  clientId: IdSchema,
  title: z.string().min(1),
  description: z.string().optional(),
  agreedWithClient: z.boolean().optional(),
  startSession: z.number().int().min(1).optional(),
  targetBehavior: z.string().optional(),
  measurementOrIndicator: z.string().optional(),
  status: GoalStatusSchema,
  relatedThemes: z.array(z.string()).optional(),
  relatedSessionIds: z.array(IdSchema).optional(),
});
export type CounselingGoal = z.infer<typeof CounselingGoalSchema>;

export const GoalProgressSchema = entity('GoalProgress', {
  clientId: IdSchema,
  goalId: IdSchema,
  sessionId: IdSchema,
  progressNote: z.string().optional(),
  evidenceIds: z.array(IdSchema).optional(),
  counselorAssessment: z.string().optional(),
  clientAssessment: z.string().optional(),
  proposedStatus: GoalStatusSchema.optional(),
  approvedStatus: GoalStatusSchema.optional(),
});
export type GoalProgress = z.infer<typeof GoalProgressSchema>;

// ---------- Case Formulation (Amendment A6) ----------

export const CaseFormulationSchema = entity('CaseFormulation', {
  clientId: IdSchema,
  /** 승인된 revision이 아직 없으면 undefined */
  currentRevisionId: IdSchema.optional(),
});
export type CaseFormulation = z.infer<typeof CaseFormulationSchema>;

export const TheoryFormulationSchema = z
  .object({
    orientation: z.string().min(1),
    formulation: z.string(),
    supportingEvidenceIds: z.array(IdSchema).optional(),
    contradictingEvidenceIds: z.array(IdSchema).optional(),
    fitLevel: z.enum(['low', 'medium', 'high']),
    questionsToVerify: z.array(z.string()).optional(),
  })
  .strict();

export const CaseFormulationRevisionSchema = entity('CaseFormulationRevision', {
  clientId: IdSchema,
  formulationId: IdSchema,
  version: z.number().int().min(1),
  previousRevisionId: IdSchema.optional(),
  basedOnSessionIds: z.array(IdSchema),

  currentProblems: z.array(z.string()).optional(),
  precipitatingFactors: z.array(z.string()).optional(),
  predisposingFactors: z.array(z.string()).optional(),
  perpetuatingFactors: z.array(z.string()).optional(),
  protectiveFactors: z.array(z.string()).optional(),

  coreEmotions: z.array(AnalysisItemSchema).optional(),
  coreCognitiveThemes: z.array(AnalysisItemSchema).optional(),
  beliefHypotheses: z.array(AnalysisItemSchema).optional(),
  behavioralPatterns: z.array(AnalysisItemSchema).optional(),
  relationalPatterns: z.array(AnalysisItemSchema).optional(),
  allianceFactors: z.array(AnalysisItemSchema).optional(),
  therapyInterferingPatterns: z.array(AnalysisItemSchema).optional(),

  theoryFormulations: z.array(TheoryFormulationSchema).optional(),

  /** 값 = AnalysisItem.id */
  addedHypotheses: z.array(IdSchema).optional(),
  strengthenedHypotheses: z.array(IdSchema).optional(),
  weakenedHypotheses: z.array(IdSchema).optional(),
  rejectedHypotheses: z.array(IdSchema).optional(),

  changeReason: z.string().optional(),
  status: z.enum(['proposal', 'approved']),
  approvedByCounselor: z.boolean(),
  approvedAt: IsoDateTimeSchema.optional(),
});
export type CaseFormulationRevision = z.infer<typeof CaseFormulationRevisionSchema>;

// ---------- Diagnostic Consideration (Amendment A7) ----------

export const DiagnosticItemSchema = z
  .object({
    id: IdSchema,
    diagnosisName: z.string().min(1),
    status: z.enum([
      'insufficient_data',
      'under_review',
      'possible',
      'professional_evaluation_recommended',
      'externally_confirmed',
    ]),
    supportingEvidenceIds: z.array(IdSchema).optional(),
    contradictoryEvidenceIds: z.array(IdSchema).optional(),
    criteriaToAssess: z.array(z.string()).optional(),
    differentialConsiderations: z.array(z.string()).optional(),
    source: z.enum(['ai-proposal', 'counselor', 'external-confirmation']),
  })
  .strict();
export type DiagnosticItem = z.infer<typeof DiagnosticItemSchema>;

export const DiagnosticConsiderationSchema = entity('DiagnosticConsideration', {
  clientId: IdSchema,
  version: z.number().int().min(1),
  previousVersionId: IdSchema.optional(),
  basedOnSessionIds: z.array(IdSchema).optional(),
  changeReason: z.string().optional(),
  symptomList: z.array(z.string()).optional(),
  considerations: z.array(DiagnosticItemSchema),
  recommendedAssessment: z.array(z.string()).optional(),
  referralRecommended: z.boolean().optional(),
  referralReason: z.string().optional(),
  referralStatus: z.string().optional(),
  status: z.enum(['proposal', 'approved']),
  approvedAt: IsoDateTimeSchema.optional(),
});
export type DiagnosticConsideration = z.infer<typeof DiagnosticConsiderationSchema>;
